<template>
  <div class="integrations-meta-view flex flex-col">
    <page-header :title="tr('Integrations')">
      <template #buttons>
        <ui-button v-if="connectButtonVisible" :disabled="isConnecting" @click="() => connect(true)">
          <loader-circle-icon v-if="isConnecting" class="animate-spin" />
          <plus-icon v-else />
          {{ tr('Connect Profile') }}
        </ui-button>
      </template>

      <!-- Hidden while Meta is the only integration; restore with more tabs. -->
      <!-- <form-step-tab :items="tabs" /> -->
    </page-header>

    <div class="flex flex-col gap-5 py-5">
      <div class="glass-panel flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-muted-foreground [&_strong]:font-semibold [&_strong]:text-foreground">
        <i class="icon meta size-5 shrink-0" />
        <span v-html="techProviderText" />
      </div>

      <empty-screen
        v-if="isProfilesEmpty"
        :icon="emptyScreenIcon"
        :title="tr('Connect your accounts to start automating')"
        :flag-text="tr('Connect profiles')"
        :description="tr('To begin using our AI-powered automation features, you\'ll need to connect your Meta Business accounts. Link your Facebook pages, Instagram profiles, and ad accounts to unlock the full potential of automated advertising.')"
        @click="handleClick"
      />
      <div v-else class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <connected-profile-item
          v-for="profile in profiles"
          :key="profile.id"
          :entity="profile"
          @remove="() => removeProfile(profile)"
          @reconnect="() => reconnect(profile)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { LoaderCircle, Plus } from '@lucide/vue';

import ConnectFacebookProfileMixin from '@/mixins/ConnectFacebookProfile';
import ToolService from '@/services/Tool.js';
import ConnectedProfileItem from '@/ui/components/ConnectedProfileItem.vue';
import DisconnectConfirmModal from '@/ui/components/DisconnectConfirmModal.vue';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
// import FormStepTab from '@/ui/components/FormStepTab.vue';
import PageHeader from '@/ui/components/PageHeader.vue';

export default {
  name: 'IntegrationsMetaView',
  components: {
    ConnectedProfileItem,
    EmptyScreen,
    // FormStepTab,
    LoaderCircleIcon: LoaderCircle,
    PlusIcon: Plus,
    PageHeader,
  },
  mixins: [ ConnectFacebookProfileMixin ],
  computed: {
    tabs() {
      return [
        {
          label: this.tr('Meta Business'),
          link: '/integrations/meta',
        },
      ];
    },
    connectButtonVisible() {
      return this.$root.isInOriginalCompany;
    },
    emptyScreenIcon() {
      return this.isConnecting ? 'loader' : 'plug';
    },
    isProfilesEmpty() {
      return this.profiles.length === 0;
    },
    profiles() {
      return this.$root.currentCompany.connected_profiles.filter(profile => profile.platform === 'facebook');
    },
    techProviderText() {
      return this.tr('<strong>:app is a Meta Tech Provider</strong> verified and approved by Meta to ensure the highest security standards.');
    },
  },
  methods: {
    handleClick() {
      if (!this.isConnecting) {
        this.connect(true);
      }
    },
    removeProfile(connectedProfile) {
      return ToolService.disconnectPrecheck({ connectedProfileId: connectedProfile.id })
        .then(resp => {
          if (resp.defunc_rules.length !== 0) {
            this.$fullscreenModal.open(
              this,
              DisconnectConfirmModal,
              {
                connectedProfile,
                defuncRules: resp.defunc_rules,
                confirmCallback: () => this.disconnect(connectedProfile),
              },
            );
          }
          else {
            return this.disconnect(connectedProfile);
          }

          return resp;
        })
        .catch(err => this.error(err));
    },
    disconnect(profile) {
      return this.$store.dispatch('auth/removeProfile', profile.id)
        .then(resp => {
          this.$toast.success(this.tr('Profile was successfully disconnected.'));

          return resp;
        })
        .catch(err => this.error(err));
    },
  },
};
</script>
